import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";
import "./Dispatch.css";

const canDispatch = (role) => role === "CHECKER_OPERATOR" || role === "OWNER";

// Audio feedback is limited to successful dispatches. No beep for failed or duplicate scans.
// Shopee uses the owner-approved original audio file from /public/gate3-shopee.mp3.
// The binary asset must be added to the branch before this PR may ship.
const SHOPEE_AUDIO_URL = "/gate3-shopee.mp3";
let currentAudio = null;

function announceSuccess(marketplace) {
  const label = String(marketplace || "").trim().toUpperCase();
  try {
    window.speechSynthesis?.cancel();
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio = null;
    }
    if (label === "SHOPEE") {
      const audio = new Audio(SHOPEE_AUDIO_URL);
      currentAudio = audio;
      audio.volume = 1;
      audio.play().catch(() => { /* no synthetic fallback: preserve owner-approved sound */ });
      return;
    }
    if (label !== "TIKTOK" || !window.speechSynthesis) return;
    // User-approved setting: automatic Indonesian voice, pitch 1.30, rate 1.20, volume 100%.
    // A short sharp lead-in is a success-only audible cue.
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(1250, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1850, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.13);
      osc.onended = () => ctx.close();
    }
    const voice = new SpeechSynthesisUtterance("TikTok!");
    voice.lang = "id-ID";
    voice.pitch = 1.30;
    voice.rate = 1.20;
    voice.volume = 1;
    const available = window.speechSynthesis.getVoices();
    const preferred = available.find((v) => /^id[-_]ID$/i.test(v.lang));
    if (preferred) voice.voice = preferred;
    window.speechSynthesis.speak(voice);
  } catch (e) { /* sound is noncritical; scan operation must not fail */ }
}

const todayWib = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const part = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};

export default function Dispatch() {
  const { user } = useAuth();
  const [resi, setResi] = useState("");
  const [result, setResult] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState({ count: 0, items: [] });
  const [historyDate, setHistoryDate] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }));
  const [history, setHistory] = useState([]);
  const [historyAwbSearch, setHistoryAwbSearch] = useState("");
  const loadHistory = useCallback(() => api.get("/dispatch/history", { params: { date: historyDate, fmt: "json" } }).then(({ data }) => setHistory(data.items || data)).catch(() => {}), [historyDate]);
  const inputRef = useRef(null);

  const [dateFrom, setDateFrom] = useState(todayWib);
  const [dateTo, setDateTo] = useState(todayWib);
  const [skuFilter, setSkuFilter] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState("");
  const downloadSkuOut = async () => {
    setExportError("");
    if (dateTo < dateFrom) { setExportError("Tanggal akhir sebelum tanggal mulai."); return; }
    setExportBusy(true);
    try {
      const { data } = await api.get("/dispatch/sku-out/export", {
        params: { from_date: dateFrom, to_date: dateTo, sku: skuFilter.trim() },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `gate3_sku_out_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError("Export gagal. Periksa akses dan tanggal lalu coba lagi.");
    } finally { setExportBusy(false); }
  };

  const loadPending = () => api.get("/dispatch/pending").then(({ data }) => setPending(data)).catch(() => {});
  const success = log.filter((r) => r.ok).length;
  const fail = log.filter((r) => !r.ok).length;
  // UI-only filter over already loaded history for the active date; no new requests.
  const historyAwbNeedle = historyAwbSearch.trim().toLowerCase();
  const visibleHistory = historyAwbNeedle ? history.filter((r) => String(r.awb || "").toLowerCase().includes(historyAwbNeedle)) : history;

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); loadPending(); }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  // Hands-free: refocus once the input is re-enabled (busy back to false) after each scan.
  useEffect(() => { if (!busy && inputRef.current) inputRef.current.focus(); }, [busy]);

  const exportHistory = async () => {
    try {
      const { data } = await api.get("/dispatch/history", { params: { date: historyDate, fmt: "xlsx" }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = `paket_keluar_${historyDate}.xlsx`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) {}
  };

  const submit = async (e) => {
    e.preventDefault();
    const code = resi.trim();
    if (!code || busy) return;
    setBusy(true); setResi("");
    let r;
    try {
      const { data } = await api.post("/dispatch/scan", { resi: code });
      r = { ...data, ts: new Date(), input: code };
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      r = { ok: false, code: "ERROR", message: msg, ts: new Date(), input: code };
    }
    setResult(r);
    if (r.ok && r.order?.marketplace) announceSuccess(r.order.marketplace);
    setLog((l) => [r, ...l].slice(0, 20));
    if (r.ok) loadPending();
    setBusy(false);
  };

  if (user && !canDispatch(user.role)) {
    return (
      <div className="app-shell"><AppTopBar active="dispatch" />
        <main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <AppTopBar active="dispatch" />
      <main className="content dispatch-gate3-content" data-testid="dispatch-gate3-content">
        <section className="panel" data-testid="dispatch-stats-panel">
          <div className="meta-grid">
            <div><span className="meta-label">Menunggu Penyerahan</span><span className="stat-big" data-testid="dispatch-pending-count">{pending.count}</span></div>
            <div><span className="meta-label">Berhasil Sesi Ini</span><span className="stat-big done" data-testid="dispatch-success-count">{success}</span></div>
            <div><span className="meta-label">Gagal Sesi Ini</span><span className="stat-big" data-testid="dispatch-fail-count">{fail}</span></div>
          </div>
        </section>

        <section className={`panel scan-status ${result ? (result.ok ? "valid" : "invalid") : ""}`} data-testid="dispatch-scan-panel">
          <div className="panel-head">
            <h2 className="panel-title">Paket Keluar — Scan RESI</h2>
          </div>
          <p className="field-help" style={{ marginBottom: 12 }}>
            Scan RESI untuk serah terima ke kurir. Inventory GOOD OUT + HPP + Piutang dibuka <b>hanya</b> saat scan RESI valid.
          </p>
          <form onSubmit={submit}>
            <label className="field-label">Scan / masukkan RESI</label>
            <input
              ref={inputRef}
              className="field-input"
              autoFocus
              value={resi}
              onChange={(e) => setResi(e.target.value)}
              placeholder="Scan RESI..."
              data-testid="dispatch-resi-input"
              disabled={busy}
            />
          </form>
          {result && (
            <div className={`scan-feedback ${result.ok ? "ok" : "bad"}`} data-testid="dispatch-feedback">
              {result.ok ? "✅ PAKET KELUAR" : "❌ GAGAL"} — {result.message}
            </div>
          )}
        </section>

        <section className="panel" data-testid="dispatch-pending-panel">
          <h2 className="panel-title">Belum Scan — Menunggu Penyerahan ({pending.count})</h2>
          <div className="table-scroll">
            <table className="data-table" data-testid="dispatch-pending-table">
              <thead><tr><th>RESI</th><th>Order</th></tr></thead>
              <tbody>
                {pending.items.length === 0 && <tr><td colSpan={2} className="empty-cell">Semua paket sudah diserahkan.</td></tr>}
                {pending.items.map((it, i) => (
                  <tr key={i} data-testid={`dispatch-pending-row-${i}`}>
                    <td>{it.resi || "-"}</td>
                    <td>{it.order}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel dispatch-report-panel" data-testid="dispatch-history-panel">
          <h2 className="panel-title dispatch-report-heading">Laporan Gate 3 — Export Excel</h2>
        <div className="dispatch-sku-export-section" data-testid="dispatch-sku-export">
          <h2 className="panel-title">Rekap SKU Keluar — Export Excel</h2>
          <p className="field-help">Tanggal OUT berdasarkan Scan Out berhasil (WIB), bukan tanggal pesanan.</p>
          <div className="dispatch-sku-export-tools">
            <label>Tanggal mulai (WIB)<input className="field-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></label>
            <label>Tanggal akhir (WIB)<input className="field-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></label>
            <label className="dispatch-sku-search-label">Cari SKU<input className="field-input" value={skuFilter} onChange={(e) => setSkuFilter(e.target.value)} placeholder="Contoh LGLUE.ACR-250" /></label>
            <button type="button" className="btn btn-primary dispatch-sku-export-button" disabled={exportBusy || !dateFrom || !dateTo} onClick={downloadSkuOut}>
              {exportBusy ? "Membuat Excel..." : "Export Excel"}
            </button>
          </div>
          {exportError && <p role="alert" className="alert-error">{exportError}</p>}
        </div>


          <hr className="dispatch-report-separator" />
          <div className="panel-head dispatch-history-head">
            <h2 className="panel-title">Riwayat Paket Keluar</h2>
            <div className="dispatch-history-tools">
              <input className="field-input dispatch-history-date" type="date" value={historyDate} onChange={(e) => setHistoryDate(e.target.value)} aria-label="Tanggal riwayat paket keluar" data-testid="dispatch-history-date" />
              <input className="field-input dispatch-history-search" type="search" value={historyAwbSearch} onChange={(e) => setHistoryAwbSearch(e.target.value)} placeholder="Cari nomor resi/AWB..." aria-label="Cari resi pada riwayat paket keluar" data-testid="dispatch-history-awb-search" />
              <button className="btn-ghost dispatch-history-export" onClick={exportHistory} data-testid="dispatch-history-export">Export Paket Keluar</button>
            </div>
          </div>
          <p className="hint">Bukti paket sudah diproses/keluar berdasarkan timestamp scan RESI. Tidak menyatakan serah-terima kurir bila event handover tidak tersedia.</p>
          <div className="dispatch-history-table-wrap">
            <table className="data-table dispatch-history-table" data-testid="dispatch-history-table">
              <thead><tr><th>Tanggal</th><th>Waktu</th><th>RESI/AWB</th><th>Order ID</th><th>Store</th><th>Status</th><th>Operator</th><th>Final Check</th><th>Batch/Import</th></tr></thead>
              <tbody>
                {visibleHistory.length === 0 && <tr><td colSpan={9} className="empty-cell">{history.length === 0 ? "Tidak ada paket keluar pada tanggal ini." : "Resi tidak ditemukan pada tanggal ini."}</td></tr>}
                {visibleHistory.map((r, i) => <tr key={r.order_id + "-" + i} data-testid="dispatch-history-row"><td data-label="Tanggal">{r.date}</td><td data-label="Waktu">{r.time}</td><td data-label="Resi/AWB" className="mono">{r.awb || "-"}</td><td data-label="Order ID" className="mono">{r.order_id}</td><td data-label="Store">{r.store || "-"}</td><td data-label="Status">{r.status}</td><td data-label="Operator">{r.checker_operator || "-"}</td><td data-label="Final Check">{r.final_check || "-"}</td><td data-label="Batch/Import">{r.batch_import || r.batch_id || "-"}</td></tr>)}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel" data-testid="dispatch-log-panel">
          <h2 className="panel-title">Riwayat Scan (sesi ini)</h2>
          <div className="table-scroll">
            <table className="data-table" data-testid="dispatch-log-table">
              <thead><tr><th>Waktu</th><th>RESI</th><th>Order</th><th>Status</th><th>Keterangan</th></tr></thead>
              <tbody>
                {log.length === 0 && <tr><td colSpan={5} className="empty-cell">Belum ada scan.</td></tr>}
                {log.map((r, i) => (
                  <tr key={i} data-testid={`dispatch-log-row-${i}`}>
                    <td>{r.ts.toLocaleTimeString("id-ID")}</td>
                    <td>{r.input}</td>
                    <td>{r.order?.source_order_id || "-"}</td>
                    <td><span className={`status-pill ${r.ok ? "on" : "off"}`}>{r.ok ? "DISPATCHED" : r.code}</span></td>
                    <td>{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
